import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { PillButton, ScribbleCircle, StickerBadge } from "../ui/decorations";

interface PricingSectionProps {
  onLoginClick: () => void;
}

/** Seção de planos: Free (branco) e Premium (ink, em destaque). */
export function PricingSection({ onLoginClick }: PricingSectionProps) {
  const { t, i18n } = useTranslation("landing");
  const list = (key: string): string[] => {
    const v = i18n.t(`landing:pricing.${key}`, { returnObjects: true });
    return Array.isArray(v) ? (v as string[]) : [];
  };
  const freeFeatures = list("freeFeatures");
  const premiumFeatures = list("premiumFeatures");
  const teamsFeatures = list("teamsFeatures");
  return (
    <section id="pricing" className="py-24 md:py-32 bg-cream">
      <div className="max-w-5xl mx-auto px-6">
        <Reveal className="text-center mb-16">
          <StickerBadge className="border-ink/15 bg-white text-ink mb-5">
            {t("pricing.badge")}
          </StickerBadge>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-ink tracking-tight text-balance">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-ink/70 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            {t("pricing.subtitle")}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
          {/* Free */}
          <Reveal>
            <div className="h-full flex flex-col rounded-3xl bg-white border-2 border-ink/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-2">
                {t("pricing.freeLabel")}
              </div>
              <div className="font-display text-4xl font-extrabold text-ink tracking-tight">
                {t("pricing.freePrice")}
              </div>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed">
                {t("pricing.freeDesc")}
              </p>
              <ul className="mt-7 space-y-3 flex-1">
                {freeFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-primary-500" />
                    <span className="text-sm text-ink/70">{f}</span>
                  </li>
                ))}
              </ul>
              <PillButton
                onClick={onLoginClick}
                className="mt-8 w-full py-3 px-4 bg-cream hover:bg-cream-dark text-ink text-sm border-2 border-ink/15"
              >
                {t("pricing.freeCta")}
                <ArrowRight size={15} />
              </PillButton>
            </div>
          </Reveal>

          {/* Premium */}
          <Reveal delay={100}>
            <div className="relative h-full flex flex-col rounded-3xl bg-ink border-2 border-primary-500 p-8 shadow-2xl transition-all duration-300 hover:-translate-y-1 md:scale-[1.03]">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <StickerBadge className="border-ink bg-primary-500 text-ink shadow-md whitespace-nowrap px-3 py-1" rotate={-3}>
                  <Star size={10} fill="currentColor" />
                  <span className="text-[11px]">{t("pricing.premiumBadge")}</span>
                </StickerBadge>
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-primary-400 mb-2">
                {t("pricing.premiumLabel")}
              </div>
              <div className="font-display text-5xl font-extrabold text-white tracking-tight">
                <ScribbleCircle>
                  {t("pricing.premiumPrice")}
                </ScribbleCircle>
                <span className="text-lg font-bold text-white/50">{t("pricing.perMonth")}</span>
              </div>
              <p className="mt-3 text-sm text-primary-200/80 leading-relaxed">
                {t("pricing.premiumDesc")}
              </p>
              <ul className="mt-7 space-y-3 flex-1">
                {premiumFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-primary-400" />
                    <span className="text-sm text-white/80">{f}</span>
                  </li>
                ))}
              </ul>
              <PillButton
                onClick={onLoginClick}
                className="mt-8 w-full py-3 px-4 bg-primary-500 hover:bg-primary-400 text-ink text-sm"
              >
                {t("pricing.premiumCta")}
                <ArrowRight size={15} />
              </PillButton>
            </div>
          </Reveal>

          {/* Teams (em breve — plano ainda não ativo) */}
          <Reveal delay={200}>
            <div className="relative h-full flex flex-col rounded-3xl bg-white border-2 border-dashed border-ink/15 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <StickerBadge className="border-ink/15 bg-ink/5 text-ink/60" rotate={3}>
                  {t("pricing.teamsSoon")}
                </StickerBadge>
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-2">
                {t("pricing.teamsLabel")}
              </div>
              <div className="font-display text-4xl font-extrabold text-ink/70 tracking-tight">
                {t("pricing.teamsSoon")}
              </div>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed">
                {t("pricing.teamsDesc")}
              </p>
              <ul className="mt-7 space-y-3 flex-1">
                {teamsFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-ink/30" />
                    <span className="text-sm text-ink/60">{f}</span>
                  </li>
                ))}
              </ul>
              <PillButton
                disabled
                className="mt-8 w-full py-3 px-4 bg-cream text-ink text-sm border-2 border-ink/15"
              >
                {t("pricing.teamsSoon")}
              </PillButton>
            </div>
          </Reveal>
        </div>

        <Reveal className="text-center mt-10">
          <p className="text-xs font-semibold text-ink/50">
            {t("pricing.footnote")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
